import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const navItems = [
  { name: 'Home', path: '/' },
  {
    name: 'Why SAFE',
    path: '/why-safe',
    submenu: [
      { name: 'Why SAFE', path: '/why-safe' },
      { name: 'Authority', path: '/network' },
      { name: 'Governance', path: '/governance' },
      { name: 'Pathway', path: '/roadmap' },
      { name: 'Advisory', path: '/advisory' }
    ]
  },
  { 
    name: 'SAFE Labs', 
    path: '/labs',
    submenu: [
      { name: 'Certification', path: '/labs' },
      { name: 'SAFEScore', path: '/safescore' },
      { name: 'Verification Readiness', path: '/safe-labs/verification-readiness' },
      { name: 'Research', path: '/research' },
      { name: 'SAIL Demo', path: '/demo' }
    ]
  },
  { name: 'SAIL', path: '/sail' },
  { name: 'Council', path: '/council' },
  { name: 'Open Letter', path: '/open-letter', highlight: true },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();

  const isActivePath = (path) => {
    if (path === '/labs') {
      return ['/labs', '/safescore', '/research', '/demo', '/safe-labs/verification-readiness'].includes(location.pathname);
    }
    if (path === '/why-safe') {
      return ['/why-safe', '/network', '/ecosystem', '/governance', '/roadmap', '/advisory'].includes(location.pathname);
    }
    return location.pathname === path;
  };

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-40" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
          >
            <img src="/logo.png" alt="Safe Standard" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <div 
                key={item.path} 
                className="relative"
                onMouseEnter={() => item.submenu && setOpenSubmenu(item.name)}
                onMouseLeave={() => setOpenSubmenu(null)}
              >
                <Link
                  to={item.path}
                  className={`py-2 flex items-center gap-1 ${
                    item.highlight
                      ? 'bg-slate-900 text-white px-4 py-1.5 text-sm font-medium uppercase tracking-wider hover:bg-slate-800'
                      : `nav-link ${isActivePath(item.path)
                        ? 'text-slate-900 border-b-2 border-slate-900' 
                        : ''}`
                  }`}
                  data-testid={`nav-link-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  {item.name}
                  {item.submenu && <ChevronDown className="w-4 h-4" />}
                </Link>
                
                {/* Submenu */}
                {item.submenu && openSubmenu === item.name && (
                  <div className="absolute top-full left-0 bg-white border border-slate-200 shadow-lg min-w-[180px] py-2 z-50">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.path}
                        to={subItem.path}
                        className={`block px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${
                          location.pathname === subItem.path 
                            ? 'text-cyan-700 font-semibold bg-slate-50' 
                            : 'text-slate-600'
                        }`}
                        data-testid={`nav-submenu-${subItem.name.toLowerCase()}`}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-slate-900" />
            ) : (
              <Menu className="w-6 h-6 text-slate-900" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden border-t border-slate-200 py-4" data-testid="mobile-menu">
            {navItems.map((item) => (
              <div key={item.path}>
                <Link
                  to={item.path}
                  className={`block py-3 nav-link ${
                    isActivePath(item.path) ? 'text-slate-900 font-semibold' : ''
                  }`}
                  onClick={() => !item.submenu && setIsOpen(false)}
                  data-testid={`mobile-nav-link-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  {item.name}
                </Link>
                {item.submenu && (
                  <div className="pl-4 border-l-2 border-slate-200 ml-2">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.path}
                        to={subItem.path}
                        className={`block py-2 text-sm ${
                          location.pathname === subItem.path 
                            ? 'text-cyan-700 font-semibold' 
                            : 'text-slate-500'
                        }`}
                        onClick={() => setIsOpen(false)}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};
